import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Modal from "../../../../../components/Modal";
import { Title, Text, SmallText, SuccessIcon, Continue, Cancel, Submit, Actions, Flex } from "../DeleteSessionModal/styles";
import { SessionInDto } from "../../../../../types";
import {
  deleteBulkSession,
  resetDeleteBulkSession,
} from "../../../../../store/actions/session";
import { hideModal } from "../../../../../store/actions/modal";
import { Icon } from "../../../../../components/Icon/styles";
import checkIcon from "../../../../../assets/icons/check-bold.svg";
import trashIcon from "../../../../../assets/icons/trash.svg";
import * as Yup from "yup";
import { useTheme } from "styled-components";
import { Formik } from "formik";
import Checkbox from "../../../../../components/FormikForm/Checkbox";
import RichTextEditor from "../../../../../components/FormikForm/RichTextEditor";
import { isFutureDate } from "../../../../../helpers/dates-hanlder";
import { validateCharacterLimitWithContext } from "../../../../../helpers/formBodyCharacterLimitValidation";
import Snackbar from "../../../../../components/Snackbar";

export const schema = Yup.object().shape({
  notify_action: Yup.array().of(Yup.string()).nullable(),
  action_notification_message: Yup.string().nullable()
    .test(
      'character-limit',
      'No puedes usar más de caracteres', // El mensaje se maneja dinámicamente en la función
      function (value) {
        return validateCharacterLimitWithContext.call(this, value, 500); // 500 caracteres máximo
      }
    ),
});

interface FormValues {
  notify_action: string[];
  action_notification_message: string
}

export const initialValues: FormValues = {
  notify_action: ["false"],
  action_notification_message: ""
};

interface ComponentProps {
  sessionToDelete: SessionInDto;
  deleteBulkSessionStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  onClose?: () => void;
}

const Component = ({ sessionToDelete, deleteBulkSessionStates, onClose }: ComponentProps) => {
  const theme = useTheme() as { colors: { [key: string]: string } }
  const dispatch = useDispatch();
  const [countdown, setCountdown] = useState(5);
  const [sessionDeleted, setSessionDeleted] = useState(false);
  const [formSubmmited, setFormSubmmited] = useState(false);

  const onSubmit = ({ values, actions }) => {
    dispatch(deleteBulkSession({
      idSessions: [sessionToDelete?.id],
      ...values,
      notify_action: values.notify_action.includes("true")
    }));
  }

  const onModalClose = useCallback(() => {
    dispatch(hideModal())
    dispatch(resetDeleteBulkSession());
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (deleteBulkSessionStates.success) {
      setSessionDeleted(true)
    }
  }, [deleteBulkSessionStates.success])

  useEffect(() => {
    if (sessionDeleted) {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown(countdown - 1);
        }, 1000);

        return () => clearTimeout(timer);
      } else {
        dispatch(hideModal())
        dispatch(resetDeleteBulkSession());
        if (onClose) onClose();
      }
    }
  }, [sessionDeleted, countdown, onClose]);

  return (
    <>
      <Modal
        name="modal-single-bulk-delete-session"
        width={deleteBulkSessionStates.success ? "320" : "500"}
        onClose={() => onModalClose()}
      >
        {deleteBulkSessionStates.success ? (
          <>
            <Flex>
              <SuccessIcon>
                <Icon icon={checkIcon} size="55px" color="#4CA154" />
              </SuccessIcon>
            </Flex>
            <div style={{ height: 28 }} />
            <Title>
              El encuentro se eliminó correctamente
            </Title>
            <div style={{ height: 8 }} />
            <Text>
              Tené en cuenta que esta acción es definitiva y no podrás volver a acceder a él.
            </Text>
            <div style={{ height: 28 }} />
            <Actions style={{ justifyContent: 'center' }}>
              <Cancel
                onClick={() => {
                  dispatch(hideModal());
                  dispatch(resetDeleteBulkSession());
                  if (onClose) onClose();
                }}
                options={{
                  type: 'filled',
                  size: 'md',
                  skin: 'purple'
                }}
              >
                Cerrar
              </Cancel>
            </Actions>
            <div style={{ height: 8 }} />
            <SmallText>Toca "Cerrar" o aguarda ({countdown} seg.)<br />para cerrar el modal </SmallText>
          </>
        ) : (
          <>
            <Title>Vas a eliminar un encuentro</Title>
            <div style={{ height: 8 }} />
            <Text>
              Estás por eliminar el encuentro "{sessionToDelete?.title}". Una vez eliminado
              no podrás recuperarlo y quienes tenían acceso al mismo ya no
              podrán verlo.
            </Text>
            <div style={{ height: 28 }} />
            <Formik
              initialValues={initialValues}
              onSubmit={(values, actions) => {
                if (onSubmit) onSubmit({ values, actions });
              }}
              validateOnChange={false}
              validateOnBlur={formSubmmited}
              validationSchema={schema}
              enableReinitialize
            >
              {({ handleSubmit, errors, touched, values, setFieldValue, setFieldError, setFieldTouched, handleChange, handleBlur, }) => {
                return (
                  <form
                    className="theme-form"
                    onSubmit={(event) => {
                      setFormSubmmited(true);
                      handleSubmit(event);
                    }}
                    id="single-bulk-delete-session-form"
                  >
                    {isFutureDate(sessionToDelete?.session_datetime) && (
                      <>
                        <Checkbox
                          name="notify_action"
                          error={errors["notify_action"]}
                          touched={touched["notify_action"]}
                          value={values["notify_action"]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          items={[
                            { label: "Enviar una notificación a tus suscriptores.", value: "true" },
                          ]}
                          options={{
                            marginBottom: 0,
                          }}
                        />

                        <div style={{ height: 20 }} />

                        <span>Agrega un mensaje personalizado explicando el motivo de la cancelación (opcional)</span>

                        <div style={{ height: 12 }} />

                        <RichTextEditor
                          name={"action_notification_message"}
                          entityType="session_reminder"
                          error={errors[`action_notification_message`]}
                          touched={touched[`action_notification_message`]}
                          value={values[`action_notification_message`]}
                          placeholder="Ingresa tu mensaje aquí"
                          showToolbar={false}
                          max={500}
                          onBlur={handleBlur}
                          options={{
                            label: "",
                            height: 100,
                            marginBottom: 20,
                          }}
                        />
                      </>
                    )}

                  </form>
                );
              }}
            </Formik>
            <div style={{ height: 28 }} />
            <Actions>
              <Cancel
                options={{
                  type: "outline",
                  skin: "purple",
                  size: "lg",
                }}
                disabled={deleteBulkSessionStates.loading}
                onClick={() => {
                  dispatch(hideModal());
                  if (onClose) onClose();
                }}
              >
                Cancelar
              </Cancel>

              <Submit
                form="single-bulk-delete-session-form"
                isSubmmiting={deleteBulkSessionStates.loading}
                options={{
                  type: "filled",
                  skin: "purple",
                  size: "lg",
                }}
                style={{ gap: 6 }}
              >
                <Icon icon={trashIcon} size="18px" color="#FFFFFF" />
                Eliminar encuentro
              </Submit>
            </Actions>
          </>
        )}
      </Modal>

      <Snackbar
        visible={deleteBulkSessionStates.success || !!deleteBulkSessionStates.error}
        options={{
          time: 2000,
          type: deleteBulkSessionStates.success ? "success" : "error",
        }}
      >
        {deleteBulkSessionStates.success
          ? "Encuentro eliminado."
          : "Error al eliminar el encuentro."}
      </Snackbar>
    </>
  );
};

const state = ({ sessionStore }) => {
  const { states: deleteBulkSessionStates } = sessionStore.deleteBulk;

  return {
    deleteBulkSessionStates,
  };
};

export default connect(state)(Component) as React.ComponentType<Pick<ComponentProps, 'sessionToDelete' | 'onClose'>>;
